import React, { useState } from "react";
import { XCircle } from "lucide-react";
import type { Store } from "../../types/store";
import type { ProductFormValues } from "../Forms/AddProductModal";
import type { DuplicateChoice, DuplicateInfo, ImportResult, RowError } from "./types";
import { parseCSV, parseExcel } from "./utils";
import api from "../../lib/axios";

import StepUpload from "./StepUpload";
import StepColumnMapping from "./StepColumnMapping";
import StepPreview from "./StepPreview";
import StepDuplicateResolution from "./StepDuplicateResolution";
import StepResults from "./StepResults";

interface Props {
  stores: Store[];
  onClose: () => void;
}

const BulkImportWizard: React.FC<Props> = ({ stores, onClose }) => {
  const [selectedStore, setSelectedStore] = useState("");
  const [step, setStep] = useState(1);
  const [rawHeaders, setRawHeaders] = useState<string[]>([]);
  const [rawData, setRawData] = useState<unknown[]>([]);
  const [columnMap, setColumnMap] = useState<Record<string, string>>({});
  const [mappedRows, setMappedRows] = useState<ProductFormValues[]>([]);
  const [duplicates, setDuplicates] = useState<DuplicateInfo[]>([]);
  const [duplicateChoices, setDuplicateChoices] = useState<Record<string, DuplicateChoice>>({});
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Step 1: file parsed → advance to mapping
  const handleFileSelected = (file: File) => {
    const ext = file.name.split(".").pop()?.toLowerCase();
    const onComplete = (headers: string[], data: unknown[]) => {
      setRawHeaders(headers);
      setRawData(data);
      setStep(2);
    };
    if (ext === "csv") parseCSV(file, onComplete);
    else parseExcel(file, onComplete);
  };

  // Step 2: mapping confirmed → build mapped rows, advance to preview
  const handleMappingComplete = () => {
    const rows = (rawData as Record<string, unknown>[]).map((row) => ({
      name: String(row[columnMap.name] ?? ""),
      category: String(row[columnMap.category] ?? ""),
      cost_price: String(row[columnMap.cost_price] ?? ""),
      selling_price: String(row[columnMap.selling_price] ?? ""),
      quantity: row[columnMap.quantity] ?? "",
      store_id: selectedStore,
    })) as ProductFormValues[];
    setMappedRows(rows);
    setStep(3);
  };

  // Step 3: submit to backend
  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await api.post(`/product/add?store_id=${selectedStore}`, mappedRows);
      const { data, errors, duplicates: dupes } = res.data as {
        data: unknown[];
        errors: RowError[];
        duplicates: DuplicateInfo[];
      };

      if (dupes && dupes.length > 0) {
        setDuplicates(dupes);
        const defaultChoices: Record<string, DuplicateChoice> = {};
        dupes.forEach((d) => { defaultChoices[d.existingId] = "update"; });
        setDuplicateChoices(defaultChoices);
        setImportResult({ created: data.length, updated: 0, errors: errors ?? [] });
        setStep(4);
      } else {
        setImportResult({ created: data.length, updated: 0, errors: errors ?? [] });
        setStep(5);
      }
    } catch (err: unknown) {
      const errorData = (err as { response?: { data?: { errors?: RowError[]; message?: string } } })
        .response?.data;
      setImportResult({
        created: 0,
        updated: 0,
        errors: errorData?.errors ?? [
          {
            row: 0,
            column: "-",
            value: "-",
            reason: errorData?.message ?? "Import failed. Please try again.",
          },
        ],
      });
      setStep(5);
    } finally {
      setSubmitting(false);
    }
  };

  // Step 4: apply duplicate resolution choices
  const handleApplyDuplicates = async () => {
    setSubmitting(true);
    let updatedCount = 0;
    const resolutionErrors: RowError[] = [];

    for (const dup of duplicates) {
      if ((duplicateChoices[dup.existingId] ?? "update") === "skip") continue;
      try {
        await api.put(`/product/update/${dup.existingId}`, {
          name: dup.incoming.name,
          category: dup.incoming.category,
          cost_price: dup.incoming.cost_price,
          selling_price: dup.incoming.selling_price,
          quantity: dup.incoming.quantity,
          store_id: selectedStore,
        });
        updatedCount++;
      } catch (err: unknown) {
        resolutionErrors.push({
          row: dup.row,
          column: "update",
          value: dup.incoming.name,
          reason:
            (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
            "Failed to update product",
        });
      }
    }

    setImportResult((prev) => ({
      created: prev?.created ?? 0,
      updated: updatedCount,
      errors: [...(prev?.errors ?? []), ...resolutionErrors],
    }));
    setSubmitting(false);
    setStep(5);
  };

  const totalSteps = duplicates.length > 0 || step >= 4 ? 5 : 4;

  return (
    <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-[999]">
      <div className="p-6 w-[700px] max-h-[90vh] overflow-y-auto bg-white rounded-lg shadow-xl">
        {/* Header */}
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-xl font-bold">Bulk Product Import</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              Step {step} of {totalSteps}
            </p>
          </div>
          <XCircle color="red" onClick={onClose} className="cursor-pointer flex-shrink-0 mt-1" />
        </div>

        {step === 1 && (
          <StepUpload
            stores={stores}
            selectedStore={selectedStore}
            onStoreChange={setSelectedStore}
            onFileSelected={handleFileSelected}
          />
        )}

        {step === 2 && (
          <StepColumnMapping
            rawHeaders={rawHeaders}
            columnMap={columnMap}
            onColumnMapChange={(field, col) =>
              setColumnMap((prev) => ({ ...prev, [field]: col }))
            }
            onContinue={handleMappingComplete}
          />
        )}

        {step === 3 && (
          <StepPreview
            mappedRows={mappedRows}
            submitting={submitting}
            onSubmit={handleSubmit}
          />
        )}

        {step === 4 && (
          <StepDuplicateResolution
            duplicates={duplicates}
            duplicateChoices={duplicateChoices}
            onChoiceChange={(id, choice) =>
              setDuplicateChoices((prev) => ({ ...prev, [id]: choice }))
            }
            onSetAll={(choice) => {
              const next: Record<string, DuplicateChoice> = {};
              duplicates.forEach((d) => { next[d.existingId] = choice; });
              setDuplicateChoices(next);
            }}
            submitting={submitting}
            onApply={handleApplyDuplicates}
          />
        )}

        {step === 5 && importResult && (
          <StepResults
            importResult={importResult}
            duplicates={duplicates}
            duplicateChoices={duplicateChoices}
            onClose={onClose}
          />
        )}
      </div>
    </div>
  );
};

export default BulkImportWizard;
